import type { AgentLine, AgentState, CadenceNoCalls, FloorRep } from "@/lib/types";
import { untilPhrase } from "./blocked-callers.js";

/*
 * Why a Ready rep gets no dialer calls (ACL-568, platform ADR-179). The dialer writes its reason
 * for each of the rep's campaigns whenever it gives them nothing, and clears it when it gives them
 * a lead. The rep's phone reads it on their own lines (`AgentLine.no_calls_reason`), the Live
 * floor beside each rep (`FloorRep.noCalls`), and the Cadence tab for the campaign's line
 * (`CadenceWork.no_calls_now`).
 */

/** The dialer's reasons (`NO_CALLS_REASONS` in the platform's `services/shared/state/dialer.py`). */
export const NO_LEAD_DUE = "no_lead_due";
export const NO_LEADS_LEFT = "no_leads_left";
export const CADENCES_PAUSED = "cadences_paused";
export const OWN_CALLERS_ONLY = "own_callers_only";

/** A campaign that cannot dial at all, by what is missing: the `dial_problems` reasons. */
const CANNOT_DIAL: Record<string, string> = {
  no_trunk: "no trunk to call out on",
  no_caller_id: "no number to call from",
  no_route: "no way to call out",
  no_cadence: "it has no cadence",
  cadence_unreadable: "its cadence cannot be read",
};

export type NoCallsTone = "warning" | "info";

export interface NoCallsLine {
  text: string;
  /** Warning when somebody has to fix something; info when the rep only has to wait. */
  tone: NoCallsTone;
}

/**
 * When the next lead is due, if there is a time still ahead to say. The dialer leaves it out when it
 * cannot time a lead (one waiting for a text, or on a call with another rep), and the phone reads
 * its lines every 30 seconds, so a time can also have passed by the time it is shown.
 */
function aheadOf(nextDueAt: string | null | undefined, now: Date): string | null {
  if (!nextDueAt) return null;
  const at = new Date(nextDueAt).getTime();
  return Number.isNaN(at) || at <= now.getTime() ? null : nextDueAt;
}

/** One reason, as the rep's phone says it about one campaign. */
function repSentence(
  campaign: string, reason: string, nextDueAt: string | null | undefined, now: Date,
): NoCallsLine {
  if (reason === NO_LEAD_DUE) {
    const due = aheadOf(nextDueAt, now);
    return due
      ? { text: `No lead on ${campaign} is due ${untilPhrase({ expires_at: due }, now)}.`, tone: "info" }
      : { text: `No lead on ${campaign} is due right now.`, tone: "info" };
  }
  if (reason === NO_LEADS_LEFT) {
    return { text: `${campaign} has no leads left to call. Tell your supervisor.`, tone: "warning" };
  }
  if (reason === CADENCES_PAUSED) return { text: "Every cadence is paused by an admin.", tone: "warning" };
  if (reason === OWN_CALLERS_ONLY) return { text: `On ${campaign} you take only your own callers.`, tone: "info" };
  const missing = CANNOT_DIAL[reason];
  if (missing) return { text: `${campaign} cannot dial: ${missing}. Tell your supervisor.`, tone: "warning" };
  return { text: `No dialer calls from ${campaign} right now.`, tone: "info" };
}

/**
 * The same for a rep, who sees no campaign (ACL-652). With no names, one sentence per campaign could
 * contradict itself, so a lead still to come is said once, at its soonest, and "no leads left" only
 * when no lead is to come at all.
 */
function repNoCalls(on: readonly AgentLine[], now: Date): NoCallsLine | null {
  const reasons = on.filter((line) => line.no_calls_reason);
  if (reasons.length === 0) return null;
  const waiting = reasons.filter((line) => line.no_calls_reason === NO_LEAD_DUE);
  const sentences: NoCallsLine[] = [];
  if (waiting.length > 0) {
    const times = waiting.map((line) => aheadOf(line.next_due_at, now));
    const soonest = times.includes(null) ? null : [...times].sort()[0]!;
    sentences.push({
      text: soonest ? `No lead is due ${untilPhrase({ expires_at: soonest }, now)}.` : "No lead is due right now.",
      tone: "info",
    });
  } else if (reasons.some((line) => line.no_calls_reason === NO_LEADS_LEFT)) {
    sentences.push({ text: "You have no leads left to call. Tell your supervisor.", tone: "warning" });
  }
  for (const line of reasons) {
    const reason = line.no_calls_reason as string;
    if (reason === NO_LEAD_DUE || reason === NO_LEADS_LEFT) continue;
    if (reason === CADENCES_PAUSED) sentences.push({ text: "Every cadence is paused by an admin.", tone: "warning" });
    else if (reason === OWN_CALLERS_ONLY) sentences.push({ text: "You take only your own callers.", tone: "info" });
    else if (CANNOT_DIAL[reason]) {
      sentences.push({ text: "The dialer cannot call for you right now. Tell your supervisor.", tone: "warning" });
    } else sentences.push({ text: "No dialer calls right now.", tone: "info" });
  }
  return {
    text: [...new Set(sentences.map((s) => s.text))].join(" "),
    tone: sentences.some((s) => s.tone === "warning") ? "warning" : "info",
  };
}

/**
 * What the rep's phone says under Ready about why no dialer calls come, or null: one sentence per
 * campaign they are signed in to that gives them nothing. Only while
 * they are Ready, and never while an offer waits for them, which the dialer gave them since.
 * `asRep`: for a rep, with no campaign named.
 */
export function noCallsLine(
  me: AgentState, now: Date = new Date(), offered = false, asRep = false,
): NoCallsLine | null {
  const record = me.state;
  if (record === null || record.state !== "available" || offered) return null;
  const on = me.lines.filter((line) => record.queues.includes(line.name));
  if (asRep) return repNoCalls(on, now);
  const sentences: NoCallsLine[] = [];
  for (const line of on) {
    const reason = line.no_calls_reason;
    if (!reason) continue;
    sentences.push(repSentence(campaignName(line), reason, line.next_due_at, now));
  }
  // Every cadence paused is said once, however many campaigns it stops.
  const texts = [...new Set(sentences.map((s) => s.text))];
  if (texts.length === 0) return null;
  return {
    text: texts.join(" "),
    tone: sentences.some((s) => s.tone === "warning") ? "warning" : "info",
  };
}

function campaignName(line: AgentLine): string {
  return line.campaign_name ?? line.name;
}

/** A reason as a supervisor reads it: the floor's cell puts `detail` under `title`, as it puts a
 *  number under a name, and the Cadence tab says `text`, both on one line. */
export interface SupervisorNoCalls extends NoCallsLine {
  title: string;
  detail: string | null;
}

function said(title: string, detail: string | null, tone: NoCallsTone, joiner = " "): SupervisorNoCalls {
  return { title, detail, tone, text: detail ? `${title}${joiner}${detail}` : title };
}

/**
 * The same reason as a supervisor reads it beside the rep, on the Live floor and the Cadence tab,
 * or null for one shown elsewhere: My calls only has its own badge.
 */
export function supervisorText(
  reason: string, nextDueAt: string | null | undefined, now: Date = new Date(),
): SupervisorNoCalls | null {
  if (reason === NO_LEAD_DUE) {
    const due = aheadOf(nextDueAt, now);
    return due
      ? said("No lead due", untilPhrase({ expires_at: due }, now), "info")
      : said("No lead due", "right now", "info");
  }
  if (reason === NO_LEADS_LEFT) return said("No leads left to call", null, "warning");
  if (reason === CADENCES_PAUSED) return said("Every cadence is paused", null, "warning");
  if (reason === OWN_CALLERS_ONLY) return null;
  const missing = CANNOT_DIAL[reason];
  return missing ? said("Cannot dial", missing, "warning", ": ") : said("No dialer calls", null, "warning");
}

/** Why the dialer gives a rep on the floor nothing from the line's campaign, while Ready. */
export function floorNoCalls(
  rep: FloorRep, campaignId: string | null, now: Date = new Date(),
): SupervisorNoCalls | null {
  if (campaignId === null || rep.state !== "available") return null;
  const found = rep.noCalls?.[campaignId];
  return found ? supervisorText(found.reason, found.nextDueAt, now) : null;
}

/** The Cadence tab's row for a rep the dialer gives nothing now. It lists every reason, those
 *  the floor shows elsewhere too, since the tab has neither the badge nor the notice. */
export function cadenceNoCalls(row: CadenceNoCalls, now: Date = new Date()): SupervisorNoCalls {
  if (row.reason === OWN_CALLERS_ONLY) return said("Takes only their own callers", null, "info");
  return supervisorText(row.reason, row.next_due_at, now) ?? said("No dialer calls", null, "warning");
}
